/**
 * @fileoverview Hook to track which saved items are organized into collections
 * 
 * Returns a Set of saved_item_id values for O(1) lookup to determine
 * if an item is already in at least one collection.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Hook to get all saved item IDs that are in at least one collection.
 * 
 * @returns {Object} Object containing:
 *   - itemsInCollections: Set<string> of saved_item_id values
 *   - isLoading: boolean indicating if the query is loading
 * 
 * @example
 * const { itemsInCollections } = useItemsInCollections();
 * const isOrganized = itemsInCollections.has(savedItemId);
 */
export function useItemsInCollections() {
  const { user } = useAuth();

  const { data: itemIds = [], isLoading } = useQuery({
    queryKey: ['items-in-collections', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('collection_items')
        .select('saved_item_id');

      if (error) {
        console.error('Error fetching items in collections:', error);
        return [];
      }

      // Return array of IDs - will be converted to Set with useMemo
      return (data || []).map(d => d.saved_item_id);
    },
    enabled: !!user,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Stabilize Set reference - only recreate when itemIds array contents change
  const itemsInCollections = useMemo(() => new Set(itemIds), [itemIds]);

  return { itemsInCollections, isLoading };
}
